/**
 * Single checklist task row. Used inside ChecklistRoomRow so that no hooks
 * are ever called inside a .map() (fixes React #185).
 * All hooks must live in this component's top level if needed; this file
 * currently uses no hooks (presentational + callbacks).
 */
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Room, Task, normalizeTask, getTaskType, getMaintenanceSettings, getQuickCheckSettings, getTaskCleaningCategory, getGuestRestockQuantity } from "@/types/checklist";
import { Camera, Check, Image as ImageIcon } from "lucide-react";
import { TaskReferencePhotoStrip } from "./TaskReferencePhotoCarousel";
import CleanerPhotoUploadGallery from "./CleanerPhotoUploadGallery";
import MaintenanceTaskUI, { MaintenanceResponseValue } from "./MaintenanceTaskUI";
import QuickCheckTaskUI from "./QuickCheckTaskUI";
import PropertyGuideCard from "./PropertyGuideCard";
import type { UploadedPhoto } from "./CleaningTab";
import type { PropertyGuideEntry } from "@/hooks/usePropertyGuideEntries";
import { cn } from "@/lib/utils";

export interface ChecklistTaskRowProps {
  task: Task;
  taskIndex: number;
  roomIndex: number;
  originalRoom: Room | undefined;
  originalRoomName: string;
  displayTaskText: string;
  taskKey: string;
  readOnly: boolean;
  isTaskCompleted: (roomName: string, taskText: string) => boolean;
  onToggleTask: (roomName: string, taskText: string) => void;
  taskBeforePhotos: Record<string, UploadedPhoto[]>;
  taskAfterPhotos: Record<string, UploadedPhoto[]>;
  maintenanceResponses: Record<string, MaintenanceResponseValue | null>;
  maintenanceNotes: Record<string, string>;
  maintenancePhotos: Record<string, UploadedPhoto[]>;
  quickCheckVerified: Record<string, boolean>;
  quickCheckPhotos: Record<string, UploadedPhoto[]>;
  taskRefPhotos: { id: string; photo_url: string; room_name: string; task_text: string | null }[];
  showRefPhoto: boolean;
  showBeforeUpload: boolean;
  showAfterUpload: boolean;
  onMaintenanceResponseChange: (taskKey: string, response: MaintenanceResponseValue | null) => void;
  onMaintenanceNotesChange: (taskKey: string, noteText: string) => void;
  onMaintenanceAddPhotos: (taskKey: string, files: File[]) => void;
  onMaintenanceRemovePhoto: (taskKey: string, id: string) => void;
  onQuickCheckVerify: (taskKey: string) => void;
  onQuickCheckAddPhotos: (taskKey: string, files: File[]) => void;
  onQuickCheckRemovePhoto: (taskKey: string, id: string) => void;
  onAddBeforePhotos: (roomName: string, taskText: string, files: File[]) => void;
  onRemoveBeforePhoto: (roomName: string, taskText: string, id: string) => void;
  onAddAfterPhotos: (roomName: string, taskText: string, files: File[]) => void;
  onRemoveAfterPhoto: (roomName: string, taskText: string, id: string) => void;
  tDynamic: (text: string) => string;
  /** Property Guide entries for "Need guidance?" (filtered by room when expanded) */
  guideEntries?: PropertyGuideEntry[];
  /** When true, row sits inside a parent with divide-y (no bottom border on the row). */
  noRowDivider?: boolean;
  /** Highlight row as the next incomplete cleaning task (teal tint). */
  isNextTask?: boolean;
}

export function ChecklistTaskRow({
  task,
  taskIndex,
  roomIndex,
  originalRoomName,
  displayTaskText,
  taskKey,
  readOnly,
  isTaskCompleted,
  onToggleTask,
  taskBeforePhotos,
  taskAfterPhotos,
  maintenanceResponses,
  maintenanceNotes,
  maintenancePhotos,
  quickCheckVerified,
  quickCheckPhotos,
  taskRefPhotos,
  showRefPhoto,
  showBeforeUpload,
  showAfterUpload,
  onMaintenanceResponseChange,
  onMaintenanceNotesChange,
  onMaintenanceAddPhotos,
  onMaintenanceRemovePhoto,
  onQuickCheckVerify,
  onQuickCheckAddPhotos,
  onQuickCheckRemovePhoto,
  onAddBeforePhotos,
  onRemoveBeforePhoto,
  onAddAfterPhotos,
  onRemoveAfterPhoto,
  tDynamic,
  originalRoom,
  guideEntries = [],
  noRowDivider = false,
  isNextTask = false,
}: ChecklistTaskRowProps) {
  const origTaskObj = originalRoom?.tasks?.[taskIndex] ? normalizeTask(originalRoom.tasks[taskIndex] as Task) : normalizeTask(task);
  const taskType = getTaskType(origTaskObj);
  const beforePhotos = taskBeforePhotos[taskKey] || [];
  const afterPhotos = taskAfterPhotos[taskKey] || [];

  if (taskType === "maintenance") {
    return (
      <MaintenanceTaskUI
        taskIndex={taskIndex}
        taskText={displayTaskText}
        roomName={originalRoomName}
        settings={getMaintenanceSettings(origTaskObj)}
        readOnly={readOnly}
        response={maintenanceResponses[taskKey] || null}
        notes={maintenanceNotes[taskKey] || ""}
        photos={maintenancePhotos[taskKey] || []}
        onResponseChange={(response) => onMaintenanceResponseChange(taskKey, response)}
        onNotesChange={(noteText) => onMaintenanceNotesChange(taskKey, noteText)}
        onAddPhotos={(files) => onMaintenanceAddPhotos(taskKey, files)}
        onRemovePhoto={(id) => onMaintenanceRemovePhoto(taskKey, id)}
      />
    );
  }

  if (taskType === "quick_check") {
    return (
      <QuickCheckTaskUI
        taskIndex={taskIndex}
        taskText={displayTaskText}
        roomName={originalRoomName}
        settings={getQuickCheckSettings(origTaskObj)}
        readOnly={readOnly}
        isVerified={quickCheckVerified[taskKey] || false}
        photos={quickCheckPhotos[taskKey] || []}
        onVerify={() => onQuickCheckVerify(taskKey)}
        onAddPhotos={(files) => onQuickCheckAddPhotos(taskKey, files)}
        onRemovePhoto={(id) => onQuickCheckRemovePhoto(taskKey, id)}
      />
    );
  }

  const completed = isTaskCompleted(originalRoomName, origTaskObj.text);

  return (
    <div
      className={
        noRowDivider
          ? "space-y-3 py-2.5 px-2 sm:px-3 sm:py-3"
          : "space-y-3 pb-3 border-b border-[color:var(--pv-divider,hsl(var(--border)))] last:border-b-0"
      }
    >
      <label
        htmlFor={`task-${roomIndex}-${taskIndex}`}
        className={cn(
          "flex min-h-[48px] cursor-pointer items-start gap-2 rounded-lg p-2 transition-[background-color,transform] duration-150 sm:gap-3 sm:min-h-[52px] sm:p-2.5",
          completed
            ? "bg-[color-mix(in_srgb,var(--pv-surface-2,hsl(var(--muted)))_35%,transparent)]"
            : isNextTask
              ? "bg-emerald-50 ring-2 ring-[color:var(--pv-teal,#0fa89a)] ring-offset-0 dark:bg-emerald-950/30 dark:ring-emerald-500/60"
              : "active:bg-[color-mix(in_srgb,var(--pv-surface-2,hsl(var(--muted)))_50%,transparent)] active:scale-[0.99]"
        )}
      >
        <span className="min-w-[20px] shrink-0 pt-0.5 text-sm font-medium tabular-nums text-muted-foreground sm:min-w-[24px]">
          {taskIndex + 1}.
        </span>
        <Checkbox
          id={`task-${roomIndex}-${taskIndex}`}
          checked={completed}
          onCheckedChange={() => onToggleTask(originalRoomName, origTaskObj.text)}
          disabled={readOnly}
          className="mt-0.5 h-5 w-5 shrink-0 rounded-full border-2 border-muted-foreground/30 transition-transform duration-200 data-[state=checked]:scale-105 data-[state=checked]:border-[color:var(--pv-teal,#0fa89a)] data-[state=checked]:bg-[color:var(--pv-teal,#0fa89a)] data-[state=checked]:text-[color:var(--pv-ink-on-accent,white)]"
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex min-w-0 items-start justify-between gap-2">
            <span
              className={cn(
                "min-w-0 break-words text-base leading-snug",
                completed ? "text-muted-foreground line-through decoration-muted-foreground/70" : "text-foreground"
              )}
            >
              {displayTaskText}
            </span>
            <div className="mt-0.5 flex shrink-0 items-start gap-1.5">
              {getTaskCleaningCategory(origTaskObj as Task) === "guest_amenities" &&
                getGuestRestockQuantity(origTaskObj as Task) != null && (
                  <Badge
                    variant="secondary"
                    className="border-[color:var(--pv-teal,#0d9488)]/35 bg-[color:var(--pv-teal-soft,rgb(240_253_250))] text-[color:var(--pv-teal-press,#0f766e)] text-xs font-semibold tabular-nums"
                    title={tDynamic("Quantity to leave out for guests before inventory")}
                  >
                    ×{getGuestRestockQuantity(origTaskObj as Task)}
                  </Badge>
                )}
              {(showBeforeUpload || showAfterUpload) && (
              <span
                className="mt-0.5 shrink-0 rounded-full border border-[color:var(--pv-line,hsl(var(--border)))] bg-[color-mix(in_srgb,var(--pv-surface-2,hsl(var(--muted)))_40%,transparent)] p-1.5 transition-transform active:scale-95"
                title={
                  showBeforeUpload && showAfterUpload
                    ? tDynamic("Before + After")
                    : showBeforeUpload
                      ? tDynamic("Before")
                      : tDynamic("After")
                }
              >
                <Camera className="h-4 w-4 text-muted-foreground" aria-hidden />
              </span>
            )}
            </div>
          </div>
          {(showBeforeUpload || showAfterUpload || origTaskObj.require_reference_photo) && (
            <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
              {origTaskObj.require_reference_photo && (
                <Badge
                  variant="outline"
                  className="gap-1 px-2 py-0.5 text-xs border-amber-400/70 bg-amber-50 text-amber-800 dark:bg-amber-950/30 dark:text-amber-200"
                  title={
                    showRefPhoto
                      ? tDynamic("Match your work to the host reference photos")
                      : tDynamic("Host hasn’t uploaded reference photos for this task yet")
                  }
                >
                  <ImageIcon className="w-3 h-3" />
                  {tDynamic("Reference required")}
                </Badge>
              )}
              {(showBeforeUpload || showAfterUpload) && (
                <Badge
                  variant="outline"
                  className={`text-xs px-2 py-0.5 gap-1 ${
                    (showBeforeUpload && beforePhotos.length === 0) || (showAfterUpload && afterPhotos.length === 0)
                      ? "border-destructive text-destructive"
                      : "border-green-500 text-green-600"
                  }`}
                >
                  <Camera className="w-3 h-3" />
                  {showBeforeUpload && showAfterUpload
                    ? tDynamic("Before + After")
                    : showBeforeUpload
                      ? tDynamic("Before")
                      : tDynamic("After")}
                  {((showBeforeUpload && beforePhotos.length > 0) || !showBeforeUpload) &&
                    ((showAfterUpload && afterPhotos.length > 0) || !showAfterUpload) && (
                      <Check className="w-3 h-3" />
                    )}
                </Badge>
              )}
            </div>
          )}
        </div>
      </label>
      {showRefPhoto && (
        <div className="rounded-lg border border-[color:var(--pv-line,hsl(var(--border)))] bg-[color-mix(in_srgb,var(--pv-surface-2,hsl(var(--muted)))_45%,transparent)] p-2.5 sm:p-3">
          <TaskReferencePhotoStrip photos={taskRefPhotos} label={tDynamic("Reference Photos")} />
        </div>
      )}
      {(showBeforeUpload || showAfterUpload) && (
        <div className="space-y-2">
          <div className="space-y-2 rounded-lg border border-[color:var(--pv-line,hsl(var(--border)))] bg-[color-mix(in_srgb,var(--pv-surface-2,hsl(var(--muted)))_35%,transparent)] p-2.5 sm:p-3">
            <p className="text-sm font-medium text-[color:var(--pv-ink,hsl(var(--foreground)))]">
              {tDynamic("Proof photos (required)")}
            </p>
            <p className="text-xs text-[color:var(--pv-ink-2,hsl(var(--muted-foreground)))]">
              {tDynamic("Upload photos to confirm this task is complete.")}
            </p>
            {showBeforeUpload && (
              <CleanerPhotoUploadGallery
                type="before"
                photos={beforePhotos}
                onAddPhotos={(files) => onAddBeforePhotos(originalRoomName, origTaskObj.text, files)}
                onRemovePhoto={(id) => onRemoveBeforePhoto(originalRoomName, origTaskObj.text, id)}
                required={true}
                disabled={readOnly}
              />
            )}
            {showAfterUpload && (
              <CleanerPhotoUploadGallery
                type="after"
                photos={afterPhotos}
                onAddPhotos={(files) => onAddAfterPhotos(originalRoomName, origTaskObj.text, files)}
                onRemovePhoto={(id) => onRemoveAfterPhoto(originalRoomName, origTaskObj.text, id)}
                required={true}
                disabled={readOnly}
              />
            )}
          </div>
          {guideEntries.length > 0 && (
            <div className="pt-1">
              <p className="text-xs text-muted-foreground mb-1.5">{tDynamic("Need guidance?")}</p>
              <PropertyGuideCard
                entries={guideEntries}
                roomName={originalRoomName}
                defaultOpen={false}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
